import { makeAutoObservable, runInAction } from "mobx";
import { makePersistable } from "mobx-persist-store";
import { createClient } from "@/core/helpers";
import { errorLogger } from "@/core/errors";
import { ApiClient } from "@/clients/ApiClient";
import { createSearchApi, type SearchApi } from "@/api/search";

export interface RecentSearchEntry {
    id: string;
    query: string;
    resultCount: number;
    timestamp: number;
}

export interface SavedSearchEntry {
    id: string;
    query: string;
    createdAt: number;
    lastUsedAt: number;
}

const MAX_RECENT_SEARCHES = 10;
const MAX_SAVED_SEARCHES = 20;
const SEARCH_TIMEOUT_MS = 5000;

export const SEARCH_TIMEOUT_MESSAGE = "Sökningen tog för lång tid. Försök igen.";
export const SEARCH_FAILURE_MESSAGE = "Sökningen misslyckades. Försök igen.";
export const SEARCH_OFFLINE_MESSAGE = "Du verkar vara offline. Lokala resultat visas.";

export class SearchStore {
    podcasts: Podcast[] = [];
    episodes: Episode[] = [];
    recentSearches: RecentSearchEntry[] = [];
    savedSearches: SavedSearchEntry[] = [];
    searchResult: Episode[] = [];
    lastQuery = "";
    loading = false;
    error: string | null = null;
    private client: ApiClient;
    private readonly search: SearchApi;
    private cachedSearchQueries: Map<string, { podcasts: Podcast[]; episodes: Episode[] }> = new Map();
    /** Counts the searches asked for, so an answer that arrives after a newer question is dropped */
    private latestSearch = 0;
    private latestPodcastSearch = 0;

    constructor(search?: SearchApi) {
        makeAutoObservable<
            SearchStore,
            "client" | "search" | "cachedSearchQueries" | "latestSearch" | "latestPodcastSearch"
        >(this, {
            client: false,
            search: false,
            cachedSearchQueries: false,
            latestSearch: false,
            latestPodcastSearch: false,
        });
        makePersistable(this, {
            name: "SearchStore",
            properties: ["recentSearches", "savedSearches"],
            storage: window.localStorage,
            expireIn: 1000 * 60 * 60 * 24 * 30,
        }).catch((error) => {
            console.error("Error persisting SearchStore", error);
        });
        this.client = createClient();
        this.search = search ?? createSearchApi(this.client);
    }

    /**
     * Auto-searches podcasts and episodes if not already cached.
     * Safe to call multiple times - only fetches once per query.
     */
    async ensureSearchResults(query: string) {
        const search = ++this.latestSearch;
        const trimmedQuery = query.trim();
        if (trimmedQuery.length < 2) {
            this.podcasts = [];
            this.episodes = [];
            this.lastQuery = "";
            this.error = null;
            this.loading = false;
            return;
        }

        // Check cache first
        // Case does not change the answer, so Tutto and tutto share one request
        const cacheKey = trimmedQuery.toLocaleLowerCase();
        const cached = this.cachedSearchQueries.get(cacheKey);
        if (cached) {
            this.podcasts = cached.podcasts;
            this.episodes = cached.episodes;
            this.lastQuery = trimmedQuery;
            this.error = null;
            this.loading = false;
            return;
        }

        this.loading = true;
        this.error = null;
        try {
            const { podcasts, episodes } = await withSearchTimeout(this.search.find(trimmedQuery), SEARCH_TIMEOUT_MS);

            // Cache the results, even when the question has moved on: asking this again is then free
            this.cachedSearchQueries.set(cacheKey, { podcasts, episodes });
            if (search !== this.latestSearch) {
                return;
            }

            runInAction(() => {
                this.podcasts = podcasts;
                this.episodes = episodes;
                this.lastQuery = trimmedQuery;
            });
        } catch (error) {
            if (search !== this.latestSearch) {
                return;
            }
            runInAction(() => {
                this.podcasts = [];
                this.episodes = [];
                this.lastQuery = trimmedQuery;
                this.error = getSearchErrorMessage(error);
            });

            errorLogger.logError(error instanceof Error ? error : new Error(String(error)), undefined, {
                action: "ensureSearchResults",
                additionalData: {
                    queryLength: trimmedQuery.length,
                },
                component: "SearchStore",
            });
        } finally {
            if (search === this.latestSearch) {
                runInAction(() => {
                    this.loading = false;
                });
            }
        }
    }

    /** Filters one podcast's episodes by `query`; an empty query, or a failed search, leaves no filter (empty result) */
    async searchEpisodesInPodcast(query: string, podcastId: number) {
        const search = ++this.latestPodcastSearch;
        if (query.length === 0) {
            this.searchResult = [];
            return;
        }

        try {
            const { episodes } = await this.client.get<EpisodesResponse>(
                `/podcasts/${podcastId}/episodes?filter=${encodeURIComponent(query)}`
            );
            if (search === this.latestPodcastSearch) {
                runInAction(() => {
                    this.searchResult = episodes;
                });
            }
        } catch (error) {
            if (search !== this.latestPodcastSearch) {
                return;
            }
            runInAction(() => {
                this.searchResult = [];
            });
            errorLogger.logError(error instanceof Error ? error : new Error(String(error)), undefined, {
                action: "searchEpisodesInPodcast",
                additionalData: { podcastId, queryLength: query.length },
                component: "SearchStore",
            });
        }
    }

    addRecentSearch(query: string, resultCount = this.resultCount) {
        const trimmedQuery = query.trim();
        if (trimmedQuery.length < 2 || resultCount <= 0) {
            return;
        }

        const existing = this.recentSearches.find(
            (search) => search.query.toLocaleLowerCase() === trimmedQuery.toLocaleLowerCase()
        );
        const nextEntry: RecentSearchEntry = {
            id: existing?.id ?? createRecentSearchId(trimmedQuery),
            query: trimmedQuery,
            resultCount,
            timestamp: Date.now(),
        };

        this.recentSearches = [nextEntry, ...this.recentSearches.filter((search) => search.id !== nextEntry.id)].slice(
            0,
            MAX_RECENT_SEARCHES
        );
    }

    removeRecentSearch(id: string) {
        this.recentSearches = this.recentSearches.filter((search) => search.id !== id);
    }

    clearRecentSearches() {
        this.recentSearches = [];
    }

    saveSearch(query: string) {
        const trimmedQuery = query.trim();
        if (trimmedQuery.length < 2) {
            return;
        }

        const existing = this.savedSearches.find(
            (search) => search.query.toLocaleLowerCase() === trimmedQuery.toLocaleLowerCase()
        );
        const nextEntry: SavedSearchEntry = {
            id: existing?.id ?? createSavedSearchId(trimmedQuery),
            query: trimmedQuery,
            createdAt: existing?.createdAt ?? Date.now(),
            lastUsedAt: Date.now(),
        };

        this.savedSearches = [nextEntry, ...this.savedSearches.filter((search) => search.id !== nextEntry.id)].slice(
            0,
            MAX_SAVED_SEARCHES
        );
    }

    markSavedSearchUsed(id: string) {
        const existing = this.savedSearches.find((search) => search.id === id);
        if (!existing) {
            return;
        }

        existing.lastUsedAt = Date.now();
    }

    removeSavedSearch(id: string) {
        this.savedSearches = this.savedSearches.filter((search) => search.id !== id);
    }

    get resultCount() {
        return this.podcasts.length + this.episodes.length;
    }
}

async function withSearchTimeout<T>(promise: Promise<T>, timeoutMs: number) {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    try {
        return await Promise.race([
            promise,
            new Promise<T>((_, reject) => {
                timeout = setTimeout(() => {
                    reject(new Error("SearchTimeout"));
                }, timeoutMs);
            }),
        ]);
    } finally {
        if (timeout) {
            clearTimeout(timeout);
        }
    }
}

function getSearchErrorMessage(error: unknown) {
    if (error instanceof Error && error.message === "SearchTimeout") {
        return SEARCH_TIMEOUT_MESSAGE;
    }

    if (typeof navigator !== "undefined" && "onLine" in navigator && navigator.onLine === false) {
        return SEARCH_OFFLINE_MESSAGE;
    }

    return SEARCH_FAILURE_MESSAGE;
}

function createRecentSearchId(query: string) {
    const normalizedQuery = query.toLocaleLowerCase().replace(/\s+/g, "-");
    return `recent-${normalizedQuery}`;
}

function createSavedSearchId(query: string) {
    const normalizedQuery = query.toLocaleLowerCase().replace(/\s+/g, "-");
    return `saved-${normalizedQuery}`;
}

export function formatRecentSearchTimestamp(timestamp: number) {
    const diff = Date.now() - timestamp;

    if (diff < 60_000) {
        return "Just nu";
    }

    if (diff < 3_600_000) {
        return `${Math.floor(diff / 60_000)} min sedan`;
    }

    if (diff < 86_400_000) {
        return `${Math.floor(diff / 3_600_000)} tim sedan`;
    }

    return new Date(timestamp).toLocaleDateString("sv-SE");
}
